import Modal from '../../components/Modal';
import CustomComponent from '../CustomComponent';
import { Events } from '../../utils/dom/html-events';
import SelectorEngine from '../../utils/dom/selector-engine';

export class HouseholdMemberCardComponent extends CustomComponent {
	static key = 'lta-household-member-card';

	static get observedAttributes() {
		return ['data-member', 'data-actions'];
	}

	constructor() {
		super();
		this.state = { openMenu: false };
	}

	connectedCallback() {
		this.render();
		this.bindEvents();
		this.initModal();

		window.addEventListener('closeAllMemberMenus', (e) => {
			if (e.detail.except !== this) {
				this.setState({ openMenu: false });
			}
		});

		this._onClick = (e) => {
			// Cancel / Exit
			if (e.target.closest('.js-confirm-cancel')) {
				e.preventDefault();
				this.closeConfirmModal();
				return;
			}

			if (e.target.closest('.js-confirm-cancel-btn')) {
				e.preventDefault();
				this.closeConfirmModal();
				return;
			}

			// Confirm remove
			if (e.target.closest('.js-confirm-remove-btn')) {
				this.dispatchEvent(
					new CustomEvent('removeMember', {
						bubbles: true,
						composed: true,
						detail: {
							url: this.currentUrl,
							member: this.currentMember
						}
					})
				);

				this.closeConfirmModal();
			}
		};

		this.addEventListener(Events.Click, this._onClick);
		if (!this._documentClickBound) {
			this._documentClickBound = true;

			document.addEventListener(Events.Click, (e) => {
				if (!this.contains(e.target)) {
					this.closeMenu();
				}
			});
		}
	}

	attributeChangedCallback() {
		this.render();
		this.bindEvents();
		this.initModal();
	}

	setState(newState) {
		this.state = { ...this.state, ...newState };
		this.render();
		this.bindEvents();
		this.initModal();
	}

	initModal() {
		const modalEl = SelectorEngine.findOne('.lta-member-remove-modal', this);
		if (!modalEl) return;

		this.modalInstance = new Modal(modalEl, { backdrop: 'static' });
	}

	updateModalContent() {
		const root = this.modalInstance?._element;
		if (!root) return;
		const {
			subTitle = '',
			title = '',
			infoText = '',
			confirmLabel = 'Confirm',
			cancelLabel = 'Cancel'
		} = this.currentModalConfig || {};

		SelectorEngine.findOne('.js-modal-subtitle', root).textContent = subTitle;
		SelectorEngine.findOne('.js-modal-title', root).textContent = title;
		SelectorEngine.findOne('.js-modal-info', root).textContent = infoText;
		SelectorEngine.findOne('.js-confirm-remove-btn', root).textContent = confirmLabel;
		SelectorEngine.findOne('.js-confirm-cancel-btn', root).textContent = cancelLabel;
	}

	openConfirmModal(member, action) {
		this.currentMember = member;
		this.currentUrl = action.url;
		this.currentModalConfig = action.modal || {};

		this.initModal();
		this.updateModalContent();
		this.modalInstance?.show();
	}

	closeConfirmModal() {
		this.modalInstance?.hide();
		this.closeMenu();
	}

	parseMember() {
		const detailsCount = Number(this.getAttribute('data-details-count') || 0);

		const details = [];
		for (let i = 0; i < detailsCount; i++) {
			details.push({
				label: this.getAttribute(`data-detail-${i}-label`),
				value: this.getAttribute(`data-detail-${i}-value`),
				type: this.getAttribute(`data-detail-${i}-type`),
				order: Number(this.getAttribute(`data-detail-${i}-order`))
			});
		}
		return {
			id: this.getAttribute('data-member-id'),
			sfid: this.getAttribute('data-member-sfid'),
			firstName: this.getAttribute('data-member-firstname'),
			lastName: this.getAttribute('data-member-lastname'),
			status: this.getAttribute('data-member-status'),
			details
		};
	}

	parseActions() {
		const count = Number(this.getAttribute('data-actions-count') || 0);
		const actions = [];

		for (let i = 0; i < count; i++) {
			actions.push({
				key: this.getAttribute(`data-action-${i}-key`),
				label: this.getAttribute(`data-action-${i}-label`),
				icon: this.getAttribute(`data-action-${i}-icon`),
				type: this.getAttribute(`data-action-${i}-type`),
				url: this.getAttribute(`data-action-${i}-url`),

				modal: {
					subTitle: this.getAttribute(`data-action-${i}-modal-subtitle`),
					title: this.getAttribute(`data-action-${i}-modal-title`),
					infoText: this.getAttribute(`data-action-${i}-modal-info`),
					confirmLabel: this.getAttribute(`data-action-${i}-modal-confirm-label`),
					cancelLabel: this.getAttribute(`data-action-${i}-modal-cancel-label`)
				}
			});
		}
		return actions;
	}

	getAge(dob) {
		if (!dob) return '';
		const birth = new Date(dob);
		const diff = Date.now() - birth.getTime();
		return Math.abs(new Date(diff).getUTCFullYear() - 1970);
	}

	toggleMenu() {
		const willOpen = !this.state.openMenu;

		if (willOpen) {
			window.dispatchEvent(new CustomEvent('closeAllMemberMenus', { detail: { except: this } }));
		}

		this.setState({ openMenu: willOpen });
	}

	closeMenu() {
		this.setState({ openMenu: false });
	}

	/* ------------------------------------
	 * Event binding
	 * ---------------------------------- */

	bindEvents() {
		const menuBtn = SelectorEngine.findOne('.js-member-menu-btn', this);

		if (menuBtn) {
			menuBtn.onclick = (e) => {
				e.stopPropagation();
				this.toggleMenu();
			};
		}

		// Handle all dynamic actions
		const actionBtns = SelectorEngine.find('.js-action', this);
		actionBtns.forEach((btn) => {
			btn.onclick = () => {
				const key = btn.dataset.key;
				const url = btn.dataset.url;
				const type = btn.dataset.type;
				const member = this.parseMember();

				if (type === 'link') {
					this.dispatchEvent(
						new CustomEvent('navigate', {
							bubbles: true,
							composed: true,
							detail: { url }
						})
					);
				}

				if (type === 'api' && key === 'remove') {
					this.openConfirmModal(
						member,
						this.actions?.find((a) => a.key === key)
					);
					return;
				}
				this.closeMenu();
			};
		});
	}

	/* ------------------------------------
	 * Helper functions
	 * ---------------------------------- */

	renderDropdown(actions) {
		return actions
			.map((a) => {
				const dangerClass = a.key === 'remove' ? 'u-text-status-red' : '';
				return `
                <div class="lta-household-member-card__dropdown-item js-action u-fw-bold ${dangerClass}"
                     data-key="${a.key}"
                     data-type="${a.type}"
                     data-url="${a.url}">
                     
                    <svg class="lta-household-member-card__dropdown-icon">
                        <use href="/static/images/icons/lta-icons.svg#${a.icon}"></use>
                    </svg>

                    <span>${a.label}</span>
                </div>
            `;
			})
			.join('');
	}

	getStatusBadge(member) {
		if (!member.status) return '';
		const status = member.status.toLowerCase();

		if (status === 'active') {
			return `
            <div class="p-household-member-success-page__status-badge lta-tag__wrapper u-body2"
                 style="background-color:#EFF8F0; color:#408627; white-space:nowrap;">
                <svg width="1em" height="1em" fill="currentColor">
                    <use href="/static/images/icons/lta-icons.svg#check-circle-2"></use>
                </svg>
                ${member.status}
            </div>`;
		}

		if (status === 'account pending') {
			return `
            <div class="p-household-member-success-page__status-badge lta-tag__wrapper u-body2"
                 style="background-color:#FFF9F6; color:#CA5009; white-space:nowrap;">
                <svg width="1em" height="1em" fill="currentColor">
                    <use href="/static/images/icons/lta-icons.svg#analog-clock"></use>
                </svg>
                ${member.status}
            </div>`;
		}

		if (status === 'invite sent') {
			return `
            <div class="p-household-member-success-page__status-badge lta-tag__wrapper u-body2"
                 style="background-color:#E8F2F8; color:#16316F; white-space:nowrap;">
                <svg width="1em" height="1em" fill="currentColor">
                    <use href="/static/images/icons/lta-icons.svg#send"></use>
                </svg>
                ${member.status}
            </div>`;
		}
		return '';
	}

	renderConfirmationModal() {
		return `
        <div class="lta-modal lta-member-remove-modal fade" tabindex="-1">
			<div class="lta-modal-dialog">
				<div class="lta-modal-content">
					<div class="u-text-right u-pt-2 u-pr-2">
						<button type="button" class="lta-btn lta-btn--md lta-btn--link js-confirm-cancel u-link3">
                             Exit<svg class="lta-icon u-ml-0" width="1.5em" height="1.5em" fill="currentColor"><use xlink:href="/static/images/icons/lta-icons.svg#close"></use></svg>
						</button>
					</div>
					<div class="u-flex u-flex-column u-gap-3 u-text-center">
						<div class="u-flex u-flex-column u-px-3 md:u-px-7 u-pt-2 u-gap-1">
							<div class="js-modal-subtitle u-body1"></div>
							<div class="js-modal-title u-title-case1"></div>
						</div>
						<div class="u-px-3 u-pb-3 md:u-px-7 md:u-pb-7">
							<div class="js-modal-info u-body1 u-mb-3"></div>
							<div class="u-flex u-flex-column u-gap-2">
								<button	type="button" class="lta-btn lta-btn--primary js-confirm-remove-btn u-w-100 u-px-0"></button>
								<button	type="button" class="lta-btn lta-btn--secondary js-confirm-cancel-btn u-w-100"></button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	`;
	}

	/* ------------------------------------
	 * Render
	 * ---------------------------------- */

	render() {
		const member = this.parseMember();
		const actions = this.parseActions();
		this.actions = actions;

		const { openMenu } = this.state;

		const details = (member.details || []).sort((a, b) => a.order - b.order);

		const detailsHtml = details
			.map((d) => {
				let value = d.value;
				if (d.type === 'dateOfBirth') {
					value = this.getAge(d.value);
				}
				return `<div class="u-body2">${d.label}: ${value}</div>`;
			})
			.join('');

		return `
            <div class="lta-household-member-card">
                <div class="u-p-3 u-rounded u-body2 u-text-grey-dark u-border u-border-grey-light u-mb-3">
                    <div class="u-flex u-flex-column u-align-start u-gap-1">
                        
                        <div class="u-flex u-justify-space-between u-align-center u-gap-1 u-w-100" style="word-break:break-word;">
                            <div class="u-caption1">${member.firstName} ${member.lastName}</div>
							${this.actions.length > 0
								? `<div>
									<svg class="lta-household-member-card__menu-icon js-member-menu-btn u-cursor-pointer"
										width="1em" height="1em" fill="currentColor">
										<use href="/static/images/icons/lta-icons.svg#horizontal-menu"></use>
									</svg>
									
									<div 
										class="js-member-dropdown lta-household-member-card__dropdown-menu"
										style="display:${openMenu ? 'block' : 'none'};"
									>
										${this.renderDropdown(actions)}
									</div>
								</div>`
								: ''
							}
                        </div>

                        <div>${detailsHtml}</div>

                        ${this.getStatusBadge(member)}

                        ${this.renderConfirmationModal()}

                    </div>
                </div>
            </div>
        `;
	}
}

export default HouseholdMemberCardComponent;
